import axios from 'axios';
import {
  ASSIGN_PROJECTS_TO_ROLES_URL,
  GET_ASSIGNED_PROJECTS_FOR_ROLE_URL,
  GET_USERS,
  GET_USER_ROLES,
  get_request_config
} from '@/utils/system_constants/constant.js';
import {
  badge_class_for_index,
  map_user,
  role_id_from_payload,
  role_name_from_payload
} from './tenant_settings_user_helpers';
import { clean_string, parse_api_data } from './tenant_settings_common_helpers';

export const map_role_to_tenant_option = (role = {}) => Object.fromEntries([
  ['key', Number(role.id || role.role_id)],
  ['title', role_name_from_payload(role)],
  ['description', role_tenant_description(role)]
]);

function role_tenant_description(role = {}) {
  return clean_string(role.description || role.role_description || 'Tenant access role');
}

async function fetch_all_role_options(component) {
  if (!component.tenant_project_id) {
    return component.role_records.length
      ? component.role_records
      : await component.fetch_roles();
  }

  const response = await axios.get(GET_USER_ROLES, get_request_config());
  const response_data = parse_api_data(response.data);
  return Array.isArray(response_data.data) ? response_data.data : [];
}

export async function load_tenant_role_options() {
  this.tenant_roles_loading = true;
  this.tenant_roles_error = '';

  try {
    const roles = await fetch_all_role_options(this);
    this.tenant_role_options = roles
      .filter((role) => Number(role.id || role.role_id) > 0)
      .map(map_role_to_tenant_option);

    if (!this.tenant_role_options.length) {
      this.tenant_roles_error = 'No roles are available.';
    }
  } catch (error) {
    this.tenant_role_options = [];
    this.tenant_roles_error = this.parse_error_message(
      error,
      'Roles could not be loaded.'
    );
  } finally {
    this.tenant_roles_loading = false;
  }
}

export async function project_ids_for_role(role_id) {
  const response = await axios.post(
    GET_ASSIGNED_PROJECTS_FOR_ROLE_URL,
    { role_id },
    get_request_config()
  );
  const response_data = parse_api_data(response.data);
  const assignments = Array.isArray(response_data.data) ? response_data.data : [];

  return assignments
    .map((assignment) => Number(assignment.project || assignment.project_id))
    .filter((project_id) => Number.isInteger(project_id) && project_id > 0);
}

async function save_role_project_assignment(component, role, selected_role_ids) {
  const existing_project_ids = await component.project_ids_for_role(role.key);
  const project_ids = existing_project_ids.filter(
    (project_id) => project_id !== component.tenant_setup_project_id
  );

  if (selected_role_ids.has(role.key)) {
    project_ids.push(component.tenant_setup_project_id);
  }

  await axios.post(
    ASSIGN_PROJECTS_TO_ROLES_URL,
    {
      role_id: role.key,
      project_ids: [...new Set(project_ids)]
    },
    get_request_config()
  );
}

export async function save_tenant_role_assignments() {
  if (!this.tenant_setup_project_id) {
    this.show_error('Create the tenant project before assigning roles.');
    return false;
  }

  this.tenant_roles_saving = true;
  const selected_role_ids = new Set(this.tenant_setup_form.roles.map(Number));

  try {
    await Promise.all(
      this.tenant_role_options.map((role) =>
        save_role_project_assignment(this, role, selected_role_ids)
      )
    );
    return true;
  } catch (error) {
    this.show_error(this.parse_error_message(error, 'Role assignments could not be saved.'));
    return false;
  } finally {
    this.tenant_roles_saving = false;
  }
}

export function map_role(role = {}, index = 0, people = []) {
  const identity = role_identity(role, index);
  const role_people = people_for_role(people, identity);

  return {
    ...identity,
    ...role_display(role, index),
    ...role_status(role),
    ...role_people_summary(role_people)
  };
}

function role_identity(role = {}, index = 0) {
  const role_id = role_id_from_payload(role);
  return {
    id: role_id || `role-${index}`,
    backend_id: Number(role_id) || null,
    title: role_name_from_payload(role),
    description: role_description(role)
  };
}

function role_description(role = {}) {
  return clean_string(role.description || role.role_description) || 'Tenant access role';
}

function people_for_role(people = [], identity = {}) {
  return people.filter((person) => person_matches_role(person, identity));
}

function person_matches_role(person = {}, identity = {}) {
  if (person.role_id && identity.backend_id) {
    return String(person.role_id) === String(identity.backend_id);
  }

  return String(person.role || '').toLowerCase() === identity.title.toLowerCase();
}

function role_display(role = {}, index = 0) {
  return {
    role_tier: role_tier(role, index),
    role_badge_class: role.is_active === false ? 'is-inactive' : badge_class_for_index(index),
    scope: role_scope(role)
  };
}

function role_tier(role = {}, index = 0) {
  if (role.is_active === false) return 'Inactive';
  return index < 3 ? `Tier ${index + 1}` : 'Read-only';
}

function role_scope(role = {}) {
  return role.assigned_project_ids?.length
    ? `${role.assigned_project_ids.length} tenant project(s)`
    : 'Tenant-wide';
}

function role_status(role = {}) {
  return {
    cap: role.is_active === false ? 'Disabled' : 'Configured in workflows'
  };
}

function role_people_summary(role_people = []) {
  return {
    people: role_people.length ? String(role_people.length) : '0',
    people_count: role_people.length
  };
}

async function fetch_roles_request(component) {
  const items = await fetch_role_items(component);
  component.role_records = items;

  if (!items.length) {
    component.roles = [];
    return items;
  }

  component.roles = items.map((role, index) => component.map_role(role, index, component.people));
  return items;
}

async function fetch_role_items(component) {
  try {
    const response = await fetch_roles_response(component);
    const response_data = parse_api_data(response.data);
    return Array.isArray(response_data.data) ? response_data.data : [];
  } catch (error) {
    component.roles_error = component.parse_error_message(error, 'Could not load configured roles.');
    return [];
  }
}

async function fetch_roles_response(component) {
  try {
    return await axios.get(
      GET_USER_ROLES,
      get_request_config({ params: component.request_params() })
    );
  } catch (error) {
    component.role_records = [];
    component.roles = [];
    component.roles_error = component.parse_error_message(error, 'Could not load configured roles.');
    return { data: { data: [] } };
  }
}

export async function fetch_roles() {
  if (this.roles_request) {
    return this.roles_request;
  }

  this.roles_loading = true;
  this.roles_error = '';
  this.roles_request = handled_roles_request(this);

  try {
    return await this.roles_request;
  } catch {
    this.role_records = [];
    this.roles = [];
    this.roles_error = 'Could not load configured roles.';
    return [];
  } finally {
    this.roles_request = null;
    this.roles_loading = false;
  }
}

function handled_roles_request(component) {
  return safe_fetch_roles_request(component);
}

async function safe_fetch_roles_request(component) {
  try {
    return await fetch_roles_request(component);
  } catch (error) {
    component.role_records = [];
    component.roles = [];
    component.roles_error = component.parse_error_message(error, 'Could not load configured roles.');
    return [];
  }
}

export async function fetch_people() {
  this.people_loading = true;
  this.people_error = '';

  try {
    const response = await axios.get(
      GET_USERS,
      get_request_config({
        params: this.request_params({ page: 1, page_size: 100 })
      })
    );
    const response_data = parse_api_data(response.data);
    const items = Array.isArray(response_data.data) ? response_data.data : [];

    this.people_records = items;
    this.people = items.map(map_user).filter((person) => person.id != null);
  } catch {
    this.people_records = [];
    this.people = [];
    this.people_error = 'Could not load people for this tenant.';
  } finally {
    this.people_loading = false;
  }
}

export async function load_roles_and_people() {
  try {
    await this.fetch_people();
    await this.fetch_roles();
  } catch (error) {
    this.roles_error = normalize_roles_error(error).message;
  }
}

function normalize_roles_error(error) {
  return error instanceof Error
    ? error
    : new Error('Could not load roles and people.');
}

export function refresh_roles_and_people() {
  return this.load_roles_and_people();
}

export const tenant_role_collection_methods = {
  map_role_to_tenant_option,
  load_tenant_role_options,
  project_ids_for_role,
  save_tenant_role_assignments,
  map_role,
  fetch_roles,
  fetch_people,
  load_roles_and_people,
  refresh_roles_and_people
};
